import { useEffect, useState } from "react";
import { SettingsField } from "@/components/v2/settings-field";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useCreateProposal,
  useCustomers,
  useOpportunities,
} from "@/hooks/use-api";

interface ProposalFormSheetProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (created: { id: string }) => void;
}

interface CustomerRow {
  contact_role_id: string;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
}

interface OpportunityRow {
  id: string;
  deal_number?: string | null;
  project_type?: string | null;
}

function customerLabel(c: CustomerRow): string {
  const name = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
  return name || c.company_name || c.contact_role_id;
}

function opportunityLabel(o: OpportunityRow): string {
  const num = o.deal_number?.trim();
  const desc = o.project_type?.trim();
  if (num && desc) return `${num} — ${desc}`;
  return num || desc || o.id;
}

export function ProposalFormSheet({
  open,
  onClose,
  onCreated,
}: ProposalFormSheetProps) {
  const createProposal = useCreateProposal();

  const [customerRoleId, setCustomerRoleId] = useState<string>("");
  const [opportunityId, setOpportunityId] = useState<string>("");
  const [projectType, setProjectType] = useState<string>("");
  const [projectNotes, setProjectNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form on open. Opportunity is intentionally cleared whenever the
  // selected customer changes so the dropdown's filter stays consistent.
  useEffect(() => {
    if (open) {
      setCustomerRoleId("");
      setOpportunityId("");
      setProjectType("");
      setProjectNotes("");
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    setOpportunityId("");
  }, [customerRoleId]);

  const customersQuery = useCustomers({ limit: 50 });
  // Filter opportunities to the selected customer when one is chosen; the API
  // honors `customer_role_id` on the list route (services/core/opportunities.ts).
  const opportunitiesQuery = useOpportunities(
    customerRoleId
      ? { limit: 50, customer_role_id: customerRoleId }
      : { limit: 50 },
  );

  const customers =
    (customersQuery.data as { customers?: CustomerRow[] } | undefined)
      ?.customers ?? [];
  const opportunities =
    (
      opportunitiesQuery.data as
        | { opportunities?: OpportunityRow[] }
        | undefined
    )?.opportunities ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customerRoleId) return;
    setError(null);
    setSubmitting(true);
    try {
      const trimmedType = projectType.trim();
      const trimmedNotes = projectNotes.trim();
      const result = await createProposal.mutateAsync({
        customer_role_id: customerRoleId,
        opportunity_id: opportunityId || undefined,
        project_type: trimmedType || undefined,
        project_notes: trimmedNotes || undefined,
      });
      onCreated?.({ id: result.proposal.id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  }

  const disableSubmit = !customerRoleId;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Create proposal</SheetTitle>
        </SheetHeader>
        <form
          id="proposal-form"
          data-testid="proposal-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto space-y-4 py-4"
        >
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <SettingsField label="Customer" required>
            {/* Native select keeps the form testable in happy-dom and works
                without Radix Portal contortions; visual styling matches the
                shadcn Input register (border, ring, focus). */}
            <select
              required
              data-testid="proposal-customer-select"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={customerRoleId}
              onChange={(e) => setCustomerRoleId(e.target.value)}
            >
              <option value="" disabled>
                Select a customer
              </option>
              {customers.map((c) => (
                <option key={c.contact_role_id} value={c.contact_role_id}>
                  {customerLabel(c)}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Opportunity">
            <select
              data-testid="proposal-opportunity-select"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={opportunityId}
              disabled={!customerRoleId}
              onChange={(e) => setOpportunityId(e.target.value)}
            >
              <option value="">None</option>
              {opportunities.map((o) => (
                <option key={o.id} value={o.id}>
                  {opportunityLabel(o)}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Title">
            <Input
              value={projectType}
              onChange={(e) => setProjectType(e.target.value)}
              placeholder="Project type, e.g. Fence install"
            />
          </SettingsField>
          <SettingsField label="Notes">
            <Textarea
              value={projectNotes}
              onChange={(e) => setProjectNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </SettingsField>
        </form>
        <FormSheetFooter
          onCancel={onClose}
          submitLabel="Create proposal"
          isSubmitting={submitting}
          disabled={disableSubmit}
          formId="proposal-form"
        />
      </SheetContent>
    </Sheet>
  );
}
